import { inject, NgModule } from '@angular/core';

import { KalSnackbarConfig, KalSnackbarService } from '@kalidea/kaligraphi';
import { Store } from '@ngxs/store';

import { provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { InMemoryCache, ApolloLink, } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';

import { Subject } from 'rxjs';
import { tap } from 'rxjs/operators';

import { LogoutAction } from 'src/app/_user/store/user/user.action';
import { environment } from 'src/environments/environment';

export const IGNORE_ALL_GRAPHQL_ERRORS = 'IGNORE_ALL_GRAPHQL_ERRORS';

/**
 * Create apollo configuration
 */
export function createApollo(httpLink: HttpLink) {

    /**
     * remove _typename on graphql object.
     */
    const cleanTypeNameLink = new ApolloLink((operation, forward) => {
        if (operation.variables) {
            const omitTypename = (key, value) => (key === '__typename' ? undefined : value);
            operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename);
        }

        return forward(operation).map((data) => data);
    });

    /**
     * manage graphqlError throught graphqlErrorSubject
     */
    const onErrorLink = onError(({graphQLErrors, operation, networkError}) => {
        let message: string;

        if (graphQLErrors) {
            message = graphQLErrors[0]?.message;
        } else if (networkError) {
            message = extractNetworkErrorMessage(networkError);
        } else {
            message = 'Erreur lors de la récupération des données';
        }

        const code = graphQLErrors?.[0]?.['code'];
        const ignoreErrors = operation.getContext()[IGNORE_ALL_GRAPHQL_ERRORS];

        GraphQLModule.graphqlErrorSubject.next({message, code, ignoreErrors});
    });

    /**
     * Extrait un message lisible depuis une erreur réseau Apollo/Angular.
     *
     * Documentation:
     * - Apollo Client Error Handling: https://www.apollographql.com/docs/react/data/error-handling/#network-errors
     * - Angular HttpErrorResponse: https://angular.dev/api/common/http/HttpErrorResponse
     * - ProgressEvent (timeout XHR): https://developer.mozilla.org/en-US/docs/Web/API/ProgressEvent
     *
     * Ticket: #59338 - Correction message "Progress Error Event"
     */
    function extractNetworkErrorMessage(error: any): string {
        // Cas 1: Erreur avec message lisible
        if (error.message && typeof error.message === 'string' && !error.message.includes('[object')) {
            return error.message;
        }

        // Cas 2: HttpErrorResponse Angular avec status 0 (timeout, CORS, connexion refusée)
        // https://angular.dev/api/common/http/HttpErrorResponse - "A status of 0 usually indicates a network error"
        if (error.status === 0) {
            return 'Connexion au serveur impossible. Veuillez réessayer ultérieurement.';
        }

        // Cas 3: Erreur HTTP standard avec statut
        if (error.status && error.statusText) {
            return `Erreur ${error.status}: ${error.statusText}`;
        }

        // Cas 4: ProgressEvent (timeout XHR natif)
        // https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/timeout_event
        if (error.constructor && error.constructor.name === 'ProgressEvent') {
            return 'Délai d\'attente dépassé. L\'opération a peut-être réussi. Veuillez vérifier avant de réessayer.';
        }

        // Cas 5: Erreur imbriquée (structure HttpErrorResponse.error)
        if (error.error && typeof error.error === 'string') {
            return error.error;
        }

        // Fallback - plus de détails en mode dev
        if (!environment.production) {
            console.error('[GraphQL Network Error]', error);
            return `Erreur réseau: ${error.constructor?.name || 'Unknown'} - voir console pour plus de détails.`;
        }
        return 'Erreur réseau. Veuillez réessayer.';
    }

    const uri = environment.graphql;
    return {
        link: ApolloLink.from([
            onErrorLink,
            cleanTypeNameLink,
            httpLink.create({uri}),
        ]),
        cache: new InMemoryCache(),
    };
}


@NgModule({
    providers: [
        provideApollo(() => {
            const httpLink = inject(HttpLink);
            return createApollo(httpLink);
        })
    ]
})
export class GraphQLModule {

    static readonly graphqlErrorSubject = new Subject();

    constructor(private snackbarService: KalSnackbarService,
                private store: Store) {

        GraphQLModule.graphqlErrorSubject.asObservable().pipe(
            tap(({message: title, code, ignoreErrors}) => {
                if ('' + code !== '999') {
                    if (!ignoreErrors) {
                        this.snackbarService.open(new KalSnackbarConfig({title}));
                    }
                } else {
                    this.store.dispatch(new LogoutAction());
                }
            })
        ).subscribe();
    }

}
